<template>
  <div class="sj-autocomplete">
    <SjDropdown
      ref="dropdown"
      placement="bottom-start"
      :trigger="trigger"
      @show="handleDropdownShow"
      @hide="handleDropdownHide"
      dropdownClass="sj-autocomplete-dropdownWrapper"
      :arrowVisible="false"
      :disabled="disabled"
      :boundariesSelector="boundariesSelector"
      :appendToBody="appendToBody"
    >
      <template v-slot:reference>
        <slot>
          <div
            class="sj-autocomplete-inputWrapper"
            @mouseenter="handleInputEnter"
            @mouseleave="handleInputLeave"
            :class="[`sj-autocomplete-size-${size}`]"
          >
            <SjInput
              ref="input"
              class="sj-autocomplete-input"
              :class="{
                'dropdown-show': dropdownShow
              }"
              :value="inputText"
              :size="size"
              :border="border"
              :placeholder="placeholderFinal"
              readonly
              :disabled="disabled"
              cursor="pointer"
            ></SjInput>
            <i
              @click="handleClear"
              :class="[
                'icon_com',
                showClear ? 'commulti_close' : 'comselect_expand',
                'sj-autocomplete-icon',
                dropdownShow && !showClear
                  ? 'sj-autocomplete-icon--show'
                  : 'sj-autocomplete-icon--hide',
                {
                  'sj-autocomplete-icon-clear': showClear,
                  disabled
                }
              ]"
            ></i>
            <!-- 多选时使用tag展示数据 -->
            <div
              class="sj-autocomplete-multiple"
              v-if="
                multiple &&
                  value &&
                  (value.length > 0 || reverseSelect) &&
                  !forceHideInputItem
              "
            >
              <template v-if="!showIcon">
                <SjTag
                  class="sj-autocomplete-multiple-tag"
                  v-for="(valueItem, index) in valueData"
                  :key="`tag${index}`"
                  type="closeonly"
                  theme="2"
                  :size="smallerSize"
                  @close="handleMultiClose(valueItem)"
                >
                  {{ valueItem && valueItem.label }}
                </SjTag>
              </template>
              <template v-else>
                <div
                  class="sj-autocomplete-multiple-imgBox"
                  v-for="(valueItem, index) in valueData"
                  :key="`img${index}`"
                  :title="valueItem.label"
                >
                  <SjImage
                    class="sj-autocomplete-multiple-img"
                    round
                    :src="valueItem.icon"
                    :label="valueItem.label"
                  ></SjImage>
                  <SjIcon
                    class="sj-autocomplete-multiple-imgClose"
                    theme="2"
                    :size="15"
                    :iconSize="6"
                    iconClass="commulti_close"
                    @click.native.stop="handleMultiClose(valueItem)"
                  >
                  </SjIcon>
                </div>
              </template>
            </div>
            <div
              class="sj-autocomplete-cover"
              v-if="disabled"
              @click.stop
            ></div>
          </div>
        </slot>
      </template>
      <template v-slot:dropdown>
        <div
          class="sj-autocomplete-dropdown"
          :style="{
            width: dropdownWidthFinal,
            maxHeight: `${maxHeight}px`
          }"
          v-sj-loading="loading"
        >
          <div class="sj-autocomplete-filter" v-if="filterable" @click.stop>
            <SjSearchInput
              class="sj-autocomplete-filter-input"
              v-model="filterText"
              theme="lightborder"
              :clearForce="filterClearable && !!filteredText"
              @input="onSearchInput"
              @search="handleFilter"
            ></SjSearchInput>
            <div class="sj-autocomplete-filter-result" v-if="filteredText">
              共{{ !loading ? filteredNum : 0 }}个结果
            </div>
          </div>
          <div class="sj-autocomplete-itemWrapper" @scroll="handleScroll">
            <div
              v-if="enableSelectAll || (enableReverseSelect && !filteredText)"
              @click.stop.capture="onSelectAllClick"
              class="sj-autocomplete-item-select-all"
            >
              <span>全选</span>
              <sj-checkbox
                size="mini"
                :value="
                  reverseSelect ? !(selectAll === 'all') : selectAll === 'all'
                "
                :indeterminate="
                  selectAll === 'indeterminate' || reverseIndeterminate
                "
              ></sj-checkbox>
            </div>
            <template v-if="!optionsEmpty">
              <AutoCompleteItem
                :reverseSelect="reverseSelect"
                :limit-text="limitText"
                v-for="(item, index) in optionsFinal"
                :value="itemValue"
                :multiple="multiple"
                :showIcon="showIcon"
                :data="item"
                :level="1"
                :key="`option${index}`"
                @change="handleChange"
              ></AutoCompleteItem>
            </template>
            <template v-else>
              <div class="sj-autocomplete-empty">
                <SjBlank v-if="!loading"></SjBlank>
              </div>
            </template>
          </div>
          <div
            v-if="multiple"
            class="sj-autocomplete-confirmBox"
            :class="{
              'sj-autocomplete-confirmBox--showIcon': showIcon
            }"
            @click.stop
          >
            <SjButton
              class="sj-autocomplete-confirmBox-button"
              size="small"
              theme="2"
              border
              @click="handleConfirm"
              >确定
            </SjButton>
          </div>
        </div>
      </template>
    </SjDropdown>
  </div>
</template>

<script>
import clonedeep from "lodash.clonedeep";
import sizeMixin from "../../../utils/sizeMixin";
import AutoCompleteItem from "./item";

export default {
  name: "SjAutoComplete",
  components: {
    AutoCompleteItem
  },
  mixins: [sizeMixin],
  props: {
    value: {},
    options: {
      type: Array,
      default: () => []
    },
    multiple: {
      type: Boolean,
      default: false
    },
    limitText: {
      type: String,
      default: ""
    },
    // 多选数量限制，如果小于0则认为不限制
    multipleLimit: {
      type: Number,
      default: -1
    },
    showIcon: {
      type: Boolean,
      default: false
    },
    filterable: {
      type: Boolean,
      default: false
    },
    filterImmediately: {
      type: Boolean,
      default: false
    },
    filterClearable: {
      type: Boolean,
      default: true
    },
    filterMethod: Function,
    async: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    border: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: ""
    },
    maxHeight: {
      type: Number,
      default: 260
    },
    dropdownWidth: {
      type: Number,
      default: 220
    },
    dropdownWidthAuto: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    trigger: {
      type: String,
      default: "click"
    },
    appendToBody: {
      type: Boolean,
      default: false
    },
    boundariesSelector: {
      type: String,
      default: "body"
    },
    total: {
      type: Number,
      default: -1
    },
    enableSelectAll: {
      type: Boolean,
      default: false
    },
    // none 全不选；indeterminate 部分选；all 全选
    selectAll: {
      type: String,
      default: "none"
    },
    enableReverseSelect: {
      type: Boolean,
      default: false
    },
    reverseEnable: {
      type: Boolean,
      default: false
    },
    clearAfterInputEmpty: {
      type: Boolean,
      default: false
    },
    getValueData: Function
  },
  data() {
    return {
      // 切换正选/反选后确定之前value值还是旧的，需要强制隐藏input框展示的图片
      forceHideInputItem: false,
      dropdownShow: false,
      inputWidth: 0,
      isHovering: false,
      filterText: "",
      filteredText: "",
      mutiValueCache: [],
      reverseSelect: false
    };
  },
  computed: {
    reverseIndeterminate() {
      let result = false;
      if (!this.enableReverseSelect) {
        return result;
      }

      if (!this.mutiValueCache.length) {
        return result;
      }

      if (
        this.mutiValueCache.length < this.total &&
        this.mutiValueCache.length > 0
      ) {
        result = true;
      }

      return result;
    },

    itemValue() {
      const { value, multiple, mutiValueCache } = this;
      if (multiple) {
        return mutiValueCache;
      }
      return value;
    },
    dropdownWidthFinal() {
      const { dropdownWidth, dropdownWidthAuto, inputWidth } = this;
      let width = dropdownWidth;
      if (dropdownWidthAuto) {
        width = inputWidth;
      }
      return `${width}px`;
    },
    showClear() {
      const { isHovering, clearable, disabled, multiple, value } = this;
      const checkReverse =
        this.enableReverseSelect &&
        Array.isArray(value) &&
        value.length < this.filteredNum;
      return !!(
        clearable &&
        !disabled &&
        isHovering &&
        (!multiple
          ? value !== null && value !== undefined
          : (Array.isArray(value) && value.length) || checkReverse)
      );
    },
    valueData() {
      return this.getSelectedList(this.value);
    },
    inputText() {
      const { valueData, multiple } = this;
      if (multiple || !valueData) {
        return "";
      }
      return valueData.label;
    },
    placeholderFinal() {
      const { placeholder, valueData } = this;
      if (
        valueData === null ||
        (Array.isArray(valueData) && valueData.length === 0)
      ) {
        return placeholder;
      }
      return "";
    },
    optionsFinal() {
      const { filteredText, options, async } = this;
      if (!filteredText || async) {
        return options;
      }
      return this.getFilterOptions(filteredText, options);
    },
    filteredNum() {
      if (this.total > 0) {
        return this.total;
      }
      return this.getFilteredNum(this.optionsFinal);
    },
    optionsEmpty() {
      const { options } = this;
      return !Array.isArray(options) || options.length === 0;
    }
  },
  watch: {
    reverseEnable: {
      immediate: true,
      handler(val) {
        this.reverseSelect = val;
        this.mutiValueCache = [];
      }
    },
    options: {
      handler() {
        this.checkMaxSelectLimit();
        if (this.enableSelectAll && this.multiple) {
          this.checkSelectAll();
        }
      }
    },

    mutiValueCache: {
      immediate: true,
      handler() {
        this.checkMaxSelectLimit();
        if (this.enableSelectAll && this.multiple) {
          this.checkSelectAll();
        }
      }
    },

    multipleLimit: {
      handler() {
        this.checkMaxSelectLimit();
      }
    }
  },
  methods: {
    // 获取缓存list中需要展示的item，如果是reverse状态就返回未选中的item
    getCacheShowListData() {
      return this.getSelectedList(this.mutiValueCache);
    },

    getSelectedList(value) {
      const { options, multiple, getValueData } = this;
      let valueData = multiple ? [] : null;
      if (typeof getValueData !== "function") {
        if (
          !(
            value === null ||
            value === undefined ||
            (Array.isArray(value) && !value.length)
          )
        ) {
          const checkValue = list => {
            list.every(item => {
              if (item) {
                if (item.options && Array.isArray(item.options)) {
                  checkValue(item.options);
                } else {
                  if (multiple) {
                    let isFind = false;
                    for (let i = 0; i < value.length; i += 1) {
                      if (item.value === value[i]) {
                        if (!this.reverseSelect) {
                          valueData.push(item);
                        }
                        isFind = true;
                        break;
                      }
                    }

                    if (this.reverseSelect && !isFind) {
                      valueData.push(item);
                    }
                  } else {
                    if (item.value === value) {
                      valueData = item;
                      return false;
                    }
                  }
                }
              }
              return true;
            });
          };
          checkValue(options);
        }
      } else {
        valueData = getValueData(value);
      }
      if (multiple && valueData.length) {
        valueData.sort((a, b) => {
          return value.indexOf(a.value) - value.indexOf(b.value);
        });
      }
      return valueData;
    },

    changeSelectAll(status) {
      this.$emit("update:selectAll", status);
    },

    // 获取有效的选中项目  排除掉隐藏的
    getAvailableValueCache(options) {
      const set = new Set();
      const deepIterationOptions = tree => {
        tree.forEach(node => {
          if (Array.isArray(node.options) && node.options.length > 0) {
            deepIterationOptions(node.options);
            return;
          }

          for (const val of this.mutiValueCache) {
            if (val !== node.value) {
              continue;
            }

            if (node && !node.hide && !node.ignoreCount && !node.disabled) {
              set.add(node.value);
              break;
            }
          }
        });
      };

      deepIterationOptions(options);
      return Array.from(set);
    },
    checkSelectAll() {
      const realCache = this.getAvailableValueCache(this.optionsFinal);
      if (realCache.length === 0) {
        this.changeSelectAll("none");
        return;
      }

      let num =
        this.total > 0
          ? this.total
          : this.getFilteredNum(this.optionsFinal, true);

      // 可能有部分禁用项目在组件外部设置为被选中，因此不能只用等于
      if (realCache.length >= num) {
        this.changeSelectAll("all");
        return;
      }

      this.changeSelectAll("indeterminate");
    },
    onSelectAllClick() {
      if (this.enableReverseSelect) {
        this.reverseSelect = !this.reverseSelect;
        this.forceHideInputItem = true;
        this.mutiValueCache = [];
        this.emitSelectChange([]);
        return;
      }
      if (this.selectAll === "all") {
        this.mutiValueCache = [];
      } else {
        const set = new Set(this.mutiValueCache);
        const deepIterationOptions = tree => {
          tree.forEach(node => {
            if (Array.isArray(node.options) && node.options.length > 0) {
              deepIterationOptions(node.options);
              return;
            }

            if (node.disabled) {
              return;
            }
            set.add(node.value);
          });
        };

        deepIterationOptions(this.optionsFinal);
        this.mutiValueCache = Array.from(set);
      }
      this.$emit("selectAllClick", this.selectAll);
    },
    checkMaxSelectLimit() {
      if (!this.multiple) {
        return;
      }

      if (
        this.multipleLimit > -1 &&
        this.mutiValueCache.length >= this.multipleLimit
      ) {
        this.changedSelectedLimitStatus(true, this.mutiValueCache);
      } else {
        this.changedSelectedLimitStatus(false);
      }
    },

    handleScroll(e) {
      if (
        Math.ceil(e.target.scrollTop + e.target.clientHeight) >=
        e.target.scrollHeight
      ) {
        this.$emit("scrollBottom");
      }
    },

    handleDropdownShow() {
      this.dropdownShow = true;
      const { value, multiple } = this;
      if (multiple) {
        this.mutiValueCache = value ? [...value] : [];
      }
      this.getInputWidth();
      this.$emit("dropdownShow");
    },
    handleDropdownHide() {
      this.dropdownShow = false;
      // 恢复反选状态
      if (this.multiple && this.enableReverseSelect) {
        this.reverseSelect = this.reverseEnable;
        this.forceHideInputItem = false;
      }
      this.$emit("dropdownHide");
    },
    handleInputEnter() {
      this.isHovering = true;
    },
    handleInputLeave() {
      this.isHovering = false;
    },
    getInputWidth() {
      const { dropdownWidthAuto } = this;
      if (dropdownWidthAuto && this.$refs.input) {
        this.inputWidth =
          this.$refs.input.$el.getBoundingClientRect().width - 2;
      } else {
        this.inputWidth = this.dropdownWidth;
      }
    },
    hideDropdown() {
      this.$refs.dropdown.doClose();
    },
    handleClear(e) {
      if (this.enableReverseSelect && this.reverseSelect) {
        this.mutiValueCache = [];
        this.emitSelectChange([]);
        this.$emit("update:reverseEnable", false);
      }

      const { showClear } = this;
      if (showClear) {
        e.stopPropagation();
        this.emitValueChange(null);
        this.hideDropdown();
      }
    },
    clearFilterText() {
      this.filterText = "";
      this.filteredText = "";
    },
    onSearchInput(val) {
      if (this.clearAfterInputEmpty && !val) {
        this.filteredText = null;
      }
      if (this.filterImmediately) {
        this.handleFilter();
      }
    },
    handleFilter() {
      const { filterText } = this;
      this.filteredText = filterText;
      if (this.async) {
        this.$emit("filter", filterText);
      }
    },
    getFilterOptions(filteredText, options) {
      let newOptions = clonedeep(options);
      const checkFilter = list => {
        const newList = [];
        list.forEach(item => {
          if (item) {
            if (item.options && Array.isArray(item.options)) {
              item.options = checkFilter(item.options);
              if (item.options.length) {
                newList.push(item);
              }
            } else {
              const { filterMethod } = this;
              if (filterMethod) {
                if (filterMethod(filteredText, item)) {
                  newList.push(item);
                }
              } else {
                const reg = new RegExp(filteredText, "i");
                if (item.label && reg.test(item.label)) {
                  newList.push(item);
                }
              }
            }
          }
        });
        return newList;
      };
      newOptions = checkFilter(newOptions);
      return newOptions;
    },

    // 选项， 是否忽略禁用项 true忽略
    getFilteredNum(options, ignoreDisabled = false) {
      let filteredNum = 0;
      const checkNum = list => {
        list.forEach(item => {
          if (item) {
            if (item.options && Array.isArray(item.options)) {
              checkNum(item.options);
            } else if (
              !item.ignoreCount &&
              !item.hide &&
              (!ignoreDisabled || !item.disabled)
            ) {
              filteredNum += 1;
            }
          }
        });
      };
      checkNum(options);
      return filteredNum;
    },
    handleMultiClose(item) {
      const { value } = this;
      const newValue = [];
      value.forEach(valItem => {
        if (valItem !== item.value) {
          newValue.push(valItem);
        }
      });

      // 如果是反选则添加进value数组
      if (this.reverseSelect) {
        newValue.push(item.value);
      }

      this.emitSelectChange(newValue);
      this.emitValueChange(newValue);
    },
    handleChange(val) {
      this.emitSelectChange(val);
      const { multiple } = this;
      if (!multiple) {
        this.hideDropdown();
      }
      if (multiple) {
        this.mutiValueCache = val;
      } else {
        this.emitValueChange(val);
      }
    },

    changedSelectedLimitStatus(enableCheck, selectedList) {
      // 递归当前options, 把未选择的选项禁用掉
      const checkLimitFunction = tree => {
        tree.forEach(node => {
          if (Array.isArray(node.options) && node.options.length > 0) {
            checkLimitFunction(node.options);
            return;
          }

          if (enableCheck) {
            node.limit = !selectedList.includes(node.value);
            return;
          }

          node.limit = false;
        });
      };

      checkLimitFunction(this.optionsFinal);
    },
    emitSelectChange(val) {
      this.$emit("selectChange", val);
    },
    emitValueChange(val) {
      this.$emit("input", val);
      this.$emit("change", val);
    },
    handleConfirm() {
      this.forceHideInputItem = false;
      if (this.reverseEnable !== this.reverseSelect) {
        this.$emit("update:reverseEnable", this.reverseSelect);
      }
      this.emitValueChange(this.mutiValueCache);
      this.hideDropdown();
    }
  }
};
</script>

<style lang="less">
.sj-autocomplete-dropdownWrapper {
  border: 0 !important;
}
</style>

<style lang="less" scoped>
@input-padding-right: 30px;
.sj-autocomplete {
  display: inline-block;
  .sj-autocomplete-inputWrapper {
    position: relative;
    .sj-autocomplete-cover {
      position: absolute;
      width: 100%;
      height: 100%;
      cursor: not-allowed;
    }
    .sj-autocomplete-multiple {
      position: absolute;
      top: 0;
      left: 0;
      right: 30px;
      height: 100%;
      cursor: pointer;
      display: flex;
      align-items: center;
      overflow: hidden;

      .sj-autocomplete-multiple-tag {
        margin-left: 3px;
        color: @color-available !important;
        background-color: @color-productlight !important;
        flex-shrink: 0;
      }

      .sj-autocomplete-multiple-imgBox {
        position: relative;
        margin-left: 6px;
        flex-shrink: 0;

        .sj-autocomplete-multiple-img {
          width: 24px !important;
          height: 24px !important;
        }

        .sj-autocomplete-multiple-imgClose {
          position: absolute;
          top: 0;
          right: -3px;
          border-radius: 50%;
          background-color: @color-gray-2;
          color: @color-normal;

          &:hover {
            background-color: @color-gray-3;
          }
        }
      }
    }
    .sj-autocomplete-input {
      padding-right: @input-padding-right!important;
      .text-ellipsis();

      &.dropdown-show {
        border-color: @color-highlight;
      }
    }

    each(@sizes, {
      &.sj-autocomplete-size-@{value} {
        .sj-autocomplete-icon {
          .icon_size(@value);
        }
      }
    });

    .sj-autocomplete-icon {
      position: absolute;
      display: inline-block;
      right: 5px;
      top: 50%;
      cursor: pointer;
      // color: @color-normal;
      transition: transform 0.3s;

      &.sj-autocomplete-icon-clear {
        font-size: 10px;
        right: 9px;
        &:hover {
          color: @color-available;
        }
      }

      &.disabled {
        color: @color-light;
        cursor: not-allowed;
      }

      &.sj-autocomplete-icon--show {
        transform: translateY(-50%) scale(0.8) rotate(-180deg);
      }

      &.sj-autocomplete-icon--hide {
        transform: translateY(-50%) scale(0.8) rotate(0);
      }
    }
  }
}

.sj-autocomplete-dropdownWrapper {
  .sj-autocomplete-dropdown {
    display: flex;
    flex-direction: column;

    .sj-autocomplete-filter {
      flex-shrink: 0;
      padding: 10px 10px 8px 10px;

      .sj-autocomplete-filter-input {
        &:hover {
          border-color: @color-highlight;
        }
      }

      .sj-autocomplete-filter-result {
        margin-top: 14px;
        text-align: left;
        .text("tiny", "light", "light");
      }
    }

    .sj-autocomplete-item-select-all {
      cursor: pointer;
      color: #97a3b4;
      font-size: 12px;
      display: flex;
      justify-content: space-between;
      padding: 0 10px;
      height: 30px;
      align-items: center;

      &:hover {
        background-color: @color-gray-2;
        color: @color-subinfo;
      }
    }

    .sj-autocomplete-itemWrapper {
      margin: 4px 0;
      flex-grow: 1;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;

      .sj-autocomplete-empty {
        height: 150px;
      }
    }

    .sj-autocomplete-confirmBox {
      flex-shrink: 0;
      padding: 10px 20px;

      &.sj-autocomplete-confirmBox--showIcon {
        border-top: 1px solid @color-gray-2;
      }

      .sj-autocomplete-confirmBox-button {
        width: 100%;
      }
    }
  }
}
</style>
